import { useEffect, useState, useMemo } from "react";
import { SupportTicketMessage } from "src/api/rtk/indorsoftRu/slices/supportApiSlice";
import { MessagesByDateGroup } from "../utils/supportTicketUtils";
import MessageItem from "./MessageItem";
import { scrollToElementTop } from "../utils/supportTicketScroll";
import { useVisibleSupportMessages } from "../hooks/useVisibleSupportMessages";
import css from "../SupportTicketPage.module.scss";

type MessagesListProps = {
    messagesByDate: MessagesByDateGroup[];
    firstNewSupportMessageId: string | null;
    newMessagesCount: number;
    messagesListRef: React.RefObject<HTMLUListElement>;
    newMessagesLabelRef: React.RefObject<HTMLLIElement>;
    ticketId: string | undefined;
    onMarkMessagesAboveAsSeenReady?: (markFn: () => void) => void;
};

export default function MessagesList({
    messagesByDate,
    firstNewSupportMessageId,
    newMessagesCount,
    messagesListRef,
    newMessagesLabelRef,
    ticketId,
    onMarkMessagesAboveAsSeenReady,
}: MessagesListProps) {
    const [showNewMessagesButton, setShowNewMessagesButton] = useState(false);

    // Получаем все сообщения из групп
    const allMessages = useMemo(() => {
        return messagesByDate.flatMap(group => group.items);
    }, [messagesByDate]);

    // Отслеживаем видимые сообщения поддержки
    const { lastSeenSupportMessageId, markMessagesAboveAsSeen } = useVisibleSupportMessages({
        messages: allMessages,
        messagesListRef,
        ticketId,
    });

    // Передаем функцию в родительский компонент
    useEffect(() => {
        if (onMarkMessagesAboveAsSeenReady && markMessagesAboveAsSeen) {
            onMarkMessagesAboveAsSeenReady(markMessagesAboveAsSeen);
        }
    }, [onMarkMessagesAboveAsSeenReady, markMessagesAboveAsSeen]);

    // Находим первое фактически непрочитанное сообщение поддержки
    const firstUnreadSupportMessage = useMemo(() => {
        return allMessages.find((message) => {
            if (message.MESSAGE_BY_SUPPORT_TEAM !== "Y") return false;
            const msgId = Number(message.ID);
            if (!Number.isFinite(msgId)) return false;
            // Непрочитанное = ID больше последнего просмотренного
            return msgId > lastSeenSupportMessageId;
        });
    }, [allMessages, lastSeenSupportMessageId]);

    // Подсчитываем количество фактически непрочитанных сообщений поддержки
    const unreadSupportMessagesCount = useMemo(() => {
        return allMessages.filter((message) => {
            if (message.MESSAGE_BY_SUPPORT_TEAM !== "Y") return false;
            const msgId = Number(message.ID);
            if (!Number.isFinite(msgId)) return false;
            // Непрочитанное = ID больше последнего просмотренного
            return msgId > lastSeenSupportMessageId;
        }).length;
    }, [allMessages, lastSeenSupportMessageId]);

    // Отслеживаем скролл для показа кнопки
    useEffect(() => {
        const list = messagesListRef.current;
        
        if (!list || !firstUnreadSupportMessage || unreadSupportMessagesCount === 0) {
            setShowNewMessagesButton(false);
            return;
        }

        const checkVisibility = () => {
            // Находим элемент первого непрочитанного сообщения
            const firstUnreadElement = list.querySelector<HTMLElement>(
                `[data-message-id="${firstUnreadSupportMessage.ID}"]`
            );
            
            if (!firstUnreadElement) {
                setShowNewMessagesButton(false);
                return;
            }

            const listRect = list.getBoundingClientRect();
            const elementRect = firstUnreadElement.getBoundingClientRect();
            
            // Проверяем, видно ли первое непрочитанное сообщение
            const isElementVisible = 
                elementRect.top >= listRect.top &&
                elementRect.bottom <= listRect.bottom;
            
            // Показываем кнопку, если первое непрочитанное сообщение не видно
            setShowNewMessagesButton(!isElementVisible);
        };

        checkVisibility();
        list.addEventListener('scroll', checkVisibility);
        
        return () => {
            list.removeEventListener('scroll', checkVisibility);
        };
    }, [messagesByDate, firstUnreadSupportMessage, unreadSupportMessagesCount, messagesListRef]);

    // Функция для скролла к первому непрочитанному сообщению
    const scrollToFirstUnread = () => {
        if (!firstUnreadSupportMessage) return;
        
        const list = messagesListRef.current;
        if (!list) return;

        const firstUnreadElement = list.querySelector<HTMLElement>(
            `[data-message-id="${firstUnreadSupportMessage.ID}"]`
        );
        
        if (firstUnreadElement) {
            scrollToElementTop(list, firstUnreadElement);
        }
    };

    return (
        <div className={css.messagesListWrapper}>
            {showNewMessagesButton && unreadSupportMessagesCount > 0 && (
                <button
                    className={css.newMessagesButton}
                    onClick={scrollToFirstUnread}
                    aria-label={`Прокрутить к ${unreadSupportMessagesCount} непрочитанным сообщениям`}
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M10 14L6 10M10 14L14 10M10 14V6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span className={css.newMessagesButtonBadge}>
                        {unreadSupportMessagesCount}
                    </span>
                </button>
            )}
            <ul ref={messagesListRef} className={css.messagesList}>
                <div className={css.freeSpacer}></div>
                {messagesByDate.map((group) => (
                    <li key={group.dateKey} className={css.messagesDateGroup}>
                        <div className={css.messagesDateLabel}>
                            {group.label}
                        </div>
                        <ul className={css.messagesDateList}>
                            {group.items.map((message: SupportTicketMessage) => {
                                const isFirstNewSupportMessage =
                                    firstNewSupportMessageId === message.ID;
                                return (
                                    <MessageItem
                                        key={message.ID}
                                        message={message}
                                        isFirstNewSupportMessage={isFirstNewSupportMessage}
                                        newMessagesLabelRef={
                                            isFirstNewSupportMessage ? newMessagesLabelRef : undefined
                                        }
                                    />
                                );
                            })}
                        </ul>
                    </li>
                ))}
            </ul>
        </div>
    );
}

